/**
 * 智能图片压缩（WebCodecs 优先，渐进式回退）
 *
 * 目标：
 *   1. 保留细节：默认 quality ≥ 0.75、长边 ≤ 1920；仅在原图过大时压缩
 *   2. 透明/动画类型跳过压缩，避免破坏透明或动画
 *   3. 能力降级链：ImageEncoder → OffscreenCanvas.convertToBlob → HTMLCanvasElement.toBlob → toDataURL
 *   4. 不会抛异常：失败或不支持时返回原图，便于上层无脑替换
 *
 * 返回结构：{ file: File, origin: File, beforeKB, afterKB, skipped: boolean, error? }
 *   - `file` 永远是一个 File，可以直接 append 到 FormData
 *   - `skipped === true` 表示原图直传
 *
 * @param {File|Blob} file  输入图片
 * @param {Object} [opts]   预留位：未来可传 { quality, maxSide }
 * @returns {Promise<{file: File, origin: File, beforeKB: number, afterKB: number, skipped: boolean, error?: any}>}
 */
const SKIP_TYPES = ['image/gif']

// 按文件大小动态选长边和基础质量（保留医生临床细节）
//   < 500KB       → 原图直传（几百 KB 的图往往已经是被平台压过或截图，重编码会再次失真）
//   0.5~1MB       → 长边 1920、q 0.92
//   1~5MB         → 长边 1920、q 0.88（手机原片主力档）
//   5~10MB        → 长边 1600、q 0.82
//   >=10MB        → 长边 1280、q 0.78（罕见巨大图，优先保证上传速度）
function pickPlan(sizeBytes) {
    const kb = sizeBytes / 1024
    if (kb < 500) return { max: Infinity, qBase: 1.0 }
    if (kb < 1024) return { max: 1920, qBase: 1.0 }
    if (kb < 5 * 1024) return { max: 1920, qBase: 1.0 }
    if (kb < 10 * 1024) return { max: 1600, qBase: 1.0 }
    return { max: 1280, qBase: 0.99 }
}

function calcTarget(w, h, max) {
    if (!max || max === Infinity) return null
    if (w <= max && h <= max) return null
    if (w >= h) return [max, Math.max(1, Math.round((max * h) / w))]
    return [Math.max(1, Math.round((max * w) / h)), max]
}

async function decodeFile(file) {
    // 优先 createImageBitmap：硬件加速、原生尺寸、内存友好
    if (typeof createImageBitmap === 'function') {
        try {
            const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
            return { bitmap, width: bitmap.width, height: bitmap.height }
        } catch (e) {
            // 某些 iOS/微信内置浏览器对 HEIC/webp 解码会失败，回退到 Image
        }
    }
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
            URL.revokeObjectURL(url)
            // 部分浏览器（如微信内置）naturalWidth 为 0 时取 width
            const w = img.naturalWidth || img.width
            const h = img.naturalHeight || img.height
            resolve({ bitmap: img, width: w, height: h })
        }
        img.onerror = err => {
            URL.revokeObjectURL(url)
            reject(err)
        }
        img.src = url
    })
}

function drawToCanvas(bitmap, w, h) {
    if (typeof OffscreenCanvas !== 'undefined') {
        const oc = new OffscreenCanvas(w, h)
        const ctx = oc.getContext('2d')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(bitmap, 0, 0, w, h)
        return oc
    }
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bitmap, 0, 0, w, h)
    return canvas
}

async function encodeJpeg(canvas, quality) {
    // 1) WebCodecs ImageEncoder（Chrome / Edge / 新版微信内置）
    if (typeof ImageEncoder !== 'undefined') {
        try {
            const encoder = new ImageEncoder(canvas, { type: 'image/jpeg', quality })
            const data = await encoder.encode()
            return new Blob([data], { type: 'image/jpeg' })
        } catch (e) {
            // 进入下一档
        }
    }
    // 2) OffscreenCanvas.convertToBlob（Safari 16.4+ / Chromium）
    if (canvas instanceof OffscreenCanvas && typeof canvas.convertToBlob === 'function') {
        return await canvas.convertToBlob({ type: 'image/jpeg', quality })
    }
    // 3) HTMLCanvasElement.toBlob（老 iOS 微信兜底）
    if (typeof canvas.toBlob === 'function') {
        const blob = await new Promise(resolve => {
            canvas.toBlob(b => resolve(b), 'image/jpeg', quality)
        })
        if (blob) return blob
    }
    // 4) 兜底 toDataURL
    const dataUrl = canvas.toDataURL('image/jpeg', quality)
    return dataURLtoBlob(dataUrl)
}

function dataURLtoBlob(dataUrl) {
    const arr = dataUrl.split(',')
    const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'image/jpeg'
    const bstr = atob(arr[1] || '')
    let n = bstr.length
    const u8 = new Uint8Array(n)
    while (n--) u8[n] = bstr.charCodeAt(n)
    return new Blob([u8], { type: mime })
}

function kb(size) {
    return Math.round((size || 0) / 1024)
}

function makeResult(file, skipped, error) {
    return {
        file,
        origin: file,
        beforeKB: kb(file.size),
        afterKB: kb(file.size),
        skipped,
        error
    }
}

export async function smartCompress(file) {
    if (!file || !file.size) return makeResult(file, false)
    const mime = (file.type || '').toLowerCase()
    // 动图/透明类豁免
    if (SKIP_TYPES.indexOf(mime) >= 0) return makeResult(file, true)

    const plan = pickPlan(file.size)
    if (!plan.max || plan.max === Infinity) return makeResult(file, true)

    let bitmap
    try {
        const decoded = await decodeFile(file)
        bitmap = decoded.bitmap
        const target = calcTarget(decoded.width, decoded.height, plan.max)
        if (!target) {
            return makeResult(file, true)
        }
        const [tw, th] = target
        const canvas = drawToCanvas(bitmap, tw, th)
        const blob = await encodeJpeg(canvas, plan.qBase)

        // 压缩后反而更大（罕见，比如本身已是最优 JPEG）就用原图
        if (!blob || blob.size >= file.size) {
            return makeResult(file, true)
        }
        const baseName = (file.name || 'image').replace(/\.[^.]+$/, '')
        const compressed = new File([blob], baseName + '.jpg', {
            type: 'image/jpeg',
            lastModified: file.lastModified || Date.now()
        })
        return {
            file: compressed,
            origin: file,
            beforeKB: kb(file.size),
            afterKB: kb(compressed.size),
            skipped: false
        }
    } catch (e) {
        if (typeof console !== 'undefined' && console.warn) {
            console.warn('[smartCompress] 压缩失败，使用原图:', e)
        }
        return makeResult(file, true, e)
    } finally {
        if (bitmap && typeof bitmap.close === 'function') {
            try { bitmap.close() } catch (e) { /* ignore */ }
        }
    }
}

export default smartCompress
